import { forwardRef, PropsWithChildren, ReactNode } from 'react'

import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer'

interface Props extends PropsWithChildren {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  title?: string | ReactNode
  alwaysDialog?: boolean
  dialogContentClassName?: string
  titleClassName?: string
  hideCloseButton?: boolean
  drawerContentClassName?: string
  fullScreenDialog?: boolean
  modal?: boolean
  dir?: 'rtl' | 'ltr'
}
export const AppDialog = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      title,
      isOpen,
      onOpenChange,
      alwaysDialog,
      dialogContentClassName,
      titleClassName,
      hideCloseButton,
      drawerContentClassName,
      fullScreenDialog,
      modal = true,
      dir = 'rtl',
    },
    ref
  ) => {
    const isMobile = useIsMobile()
    if (isMobile && !alwaysDialog) {
      return (
        <Drawer open={isOpen} onOpenChange={onOpenChange}>
          <DrawerContent dir={dir} className={cn('min-h-[20vh]', drawerContentClassName)}>
            <DrawerHeader className={cn(!title && 'sr-only')}>
              <DrawerTitle className={titleClassName}>{title}</DrawerTitle>
            </DrawerHeader>
            <div className='flex grow flex-col px-4 py-2'>{children}</div>
          </DrawerContent>
        </Drawer>
      )
    }
    const dialogInnerContent = (
      <>
        <DialogHeader className={cn('flex-shrink-0 p-6 pb-0', !title && 'sr-only')}>
          <DialogTitle className={title ? titleClassName : 'sr-only'}>{title || 'Dialog'}</DialogTitle>
          <DialogDescription className='sr-only'>Dialog content</DialogDescription>
        </DialogHeader>
        <div className='min-h-0 flex-1 pt-0'>{children}</div>
      </>
    )
    const dialogClassName = cn(
      'flex max-w-[1200px] flex-col overflow-hidden p-0 rounded-[12px] border-none',
      fullScreenDialog && 'h-full w-[100vw] max-w-none',
      !title && 'pt-6',
      dialogContentClassName
    )
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange} modal={modal}>
        {isOpen && !modal && (
          <div
            className={cn(
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80'
            )}
          ></div>
        )}
        <DialogContent showCloseButton={!hideCloseButton} dir={dir} ref={ref} className={cn(dialogClassName, 'p-6')}>
          {dialogInnerContent}
        </DialogContent>
      </Dialog>
    )
  }
)
AppDialog.displayName = 'AppDialog'
